import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getContactDetails } from "../../api/account";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { config } from "../../config";
import { useIdentity } from "../../auth/useIdentity";
import { loadMsg91Widget, WidgetError, type Msg91Widget } from "../../auth/msg91Widget";
import { isTestCodeRequest, maskMobile, normalizeIndianMobile, testCodeAccessToken } from "../../api/login";
import { changePassword, changePasswordBySms, describeRules, getPasswordRules, passwordErrorText, passwordProblem, sendAccountCode } from "../../api/password";
import { ApiError } from "../../api/errors";
import { CodeChannelChoice, codeChannels, codeSentLine, type CodeChannel } from "../../components/CodeChannelChoice";
import { PasswordField } from "../../components/PasswordField";

/** Seconds before "Resend code" unlocks, as on SignIn (owner 2026-10-23: every send-code screen waits, then allows a resend). */
const SMS_RESEND_AFTER = 35;

/**
 * Edit profile › Change password: the signed-in person's OWN sign-in
 * password (owner 2026-10-22). Staff, operators and sub-operators alike.
 *
 * Two ways to prove it is them (owner 2026-10-23: "If operator don't know the
 * old or current password"): the CURRENT password, or a fresh SMS code to the
 * login's own verified mobile -- for someone who always signs in with a code
 * and has never known a password.
 *
 * 🔑 Always the caller's own namespace, never the one typed into the
 * namespace selector above: radaccess acts on the token's subject, and the
 * namespace is only its containment. A forgotten password is reset from the
 * sign-in screen instead ("Forgot password?").
 */
export function ChangePasswordCard() {
  const identity = useIdentity();
  // The TOKEN's namespace, as Sign-in & security: a provider the operator has
  // entered is not where its own login lives.
  const ns = identity.rootNamespace ?? identity.namespace ?? "";
  const widgetCfg = config().msg91;
  const rules = useQuery({
    queryKey: ["passwordRules", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: () => getPasswordRules(identity, ns),
  });
  const [by, setBy] = useState<"current" | "sms">("current");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [touched, setTouched] = useState(false);

  // The SMS way: the widget sends the code to the number typed; radaccess sets
  // the password only when that number is this login's verified mobile.
  const widgetRef = useRef<Msg91Widget | null>(null);
  // Owner 2026-10-25: the code goes to the login's OWN verified mobile; the
  // page never asks for a number (radaccess uses the login's own anyway).
  const contact = useQuery({
    queryKey: ["contactDetails", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: ({ signal }) => getContactDetails(identity, ns, signal),
  });
  const mobile = contact.data?.mobileVerified ? contact.data.mobile : "";
  const [reqId, setReqId] = useState("");
  const [masked, setMasked] = useState("");
  const [code, setCode] = useState("");
  // Where the code goes (owner 2026-10-25): SMS, Email or Both; and where it went.
  const [codeTo, setCodeTo] = useState<CodeChannel>("sms");
  const [sentLine, setSentLine] = useState("");
  const [sending, setSending] = useState(false);
  const [smsError, setSmsError] = useState("");
  const [resendUntil, setResendUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const resendIn = Math.max(0, Math.ceil((resendUntil - now) / 1000));
  const setResendIn = (seconds: number) => {
    const t = Date.now();
    setNow(t);
    setResendUntil(t + seconds * 1000);
  };
  // Counted against a deadline, as on SignIn: a throttled background tab still
  // reads the right number when it wakes.
  useEffect(() => {
    if (resendUntil <= now) return;
    const t = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(t);
  }, [resendUntil, now]);

  const save = useMutation({
    mutationFn: async () => {
      if (by === "current") return changePassword(identity, ns, current, next);
      const w = widgetRef.current;
      if (!reqId || (!w && !isTestCodeRequest(reqId))) throw new Error("no code sent");
      let token: string;
      try {
        token = isTestCodeRequest(reqId) ? testCodeAccessToken(reqId, code) : await w!.verify(code, reqId);
      } catch {
        throw new Error("code");
      }
      return changePasswordBySms(identity, ns, mobile, token, next);
    },
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setAgain("");
      setCode("");
      setReqId("");
      setTouched(false);
    },
    onError: (err) => {
      // A widget token is single-use: after a refusal only a new code helps.
      if (by === "sms") {
        setCode("");
        if (!(err instanceof Error && err.message === "code")) setReqId("");
      }
    },
  });

  const sendCode = async (channel: "sms" | "voice" = "sms") => {
    setSmsError("");
    save.reset();
    const e164 = normalizeIndianMobile(mobile);
    if (e164 === null) {
      setSmsError("Your login has no verified mobile number. Add or verify it in My contact details first.");
      return;
    }
    setSending(true);
    // ONE code from radaccess to this login's own mobile and verified email
    // (owner 2026-10-25); the widget only when radaccess cannot make codes.
    if (codeTo !== "sms" || !reqId || isTestCodeRequest(reqId)) {
      try {
        const c = await sendAccountCode(identity, ns, codeChannels(codeTo));
        widgetRef.current = null;
        setReqId(c.requestId);
        setMasked(maskMobile(e164));
        setSentLine(codeSentLine(maskMobile(e164), c.sms, c.email));
        setCode("");
        setResendIn(c.resendIn);
        setSending(false);
        return;
      } catch (err) {
        const body = err instanceof ApiError ? ((err.body ?? {}) as { error?: unknown }) : {};
        if (!(err instanceof ApiError && err.status === 503 && body.error === "login_codes_unavailable")) {
          setSmsError(
            body.error === "no_verified_email" ? "This login has no verified email address. Choose SMS."
              : body.error === "sms_required" ? "Your mobile is not verified yet, so the code must go by SMS. Choose SMS or Both."
                : err instanceof ApiError && err.status === 429 ? "Too many codes. Wait a little and try again."
                  : "The code could not be sent. Try again.",
          );
          setSending(false);
          return;
        }
        if (codeTo !== "sms") {
          setSmsError("Codes by email are not available right now. Choose SMS.");
          setSending(false);
          return;
        }
      }
    }
    if (!widgetCfg) {
      setSmsError("Codes are not available right now. Try again later.");
      setSending(false);
      return;
    }
    try {
      const w = widgetRef.current ?? (await loadMsg91Widget(widgetCfg));
      widgetRef.current = w;
      if (reqId && channel === "voice") await w.resend("voice", reqId);
      else if (reqId) await w.resend("sms", reqId);
      else setReqId(await w.send(e164));
      setMasked(maskMobile(e164));
      setCode("");
      setResendIn(SMS_RESEND_AFTER);
    } catch (err) {
      setSmsError(err instanceof WidgetError && err.throttled ? "The SMS service is busy. Wait a minute and try again." : "The code could not be sent. Try again.");
    } finally {
      setSending(false);
    }
  };

  const switchTo = (to: "current" | "sms") => {
    setBy(to);
    setCurrent("");
    setCode("");
    setReqId("");
    setSmsError("");
    save.reset();
  };

  const problem = passwordProblem(next, rules.data ?? null);
  const mismatch = again !== "" && again !== next;
  const same = by === "current" && next !== "" && next === current;
  const proof = by === "current" ? Boolean(current) : Boolean(reqId && code.length >= 4);
  const ready = Boolean(ns && proof && next && again) && !problem && !mismatch && !same && !save.isPending;
  const failure = save.error instanceof Error && save.error.message === "code"
    ? "That code is not right. Check it and try again."
    : save.error instanceof Error && save.error.message === "no code sent"
      ? "Send a code first."
      : passwordErrorText(save.error);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Change password
      </Typography>
      {/* Owner 2026-10-25: show whose password this is. */}
      {identity.username && (
        <Typography variant="body2" sx={{ mb: 1 }} data-testid="password-username">
          Username: <strong>{identity.username}</strong>
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {by === "current"
          ? "The password you sign in with. Confirm it is you with your current password."
          : "The password you sign in with. Confirm it is you with a code sent by SMS to the mobile number you sign in with."}
      </Typography>
      {!ns && <Alert severity="warning">This login has no namespace, so its password cannot be changed here.</Alert>}
      <Stack spacing={2} sx={{ maxWidth: 420 }} component="form" autoComplete="on"
        onSubmit={(e) => { e.preventDefault(); if (ready) save.mutate(); }}>
        {by === "current" ? (
          <PasswordField label="Current password" autoComplete="current-password" size="small"
            value={current} onChange={(e) => { setCurrent(e.target.value); save.reset(); }} disabled={!ns} />
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Typography variant="body2" sx={{ flex: 1, alignSelf: "center" }} data-testid="password-sms-mobile">
                {mobile
                  ? <>The code goes to your mobile <strong>{maskMobile(mobile)}</strong>.</>
                  : contact.isLoading ? "Reading your mobile number…" : "Your login has no verified mobile number. Add or verify it in My contact details first."}
              </Typography>
              <Button variant="outlined" sx={{ height: 40, whiteSpace: "nowrap" }} disabled={!ns || sending || !mobile || resendIn > 0}
                onClick={() => void sendCode("sms")} data-testid="password-sms-send">
                {resendIn > 0 ? `Resend code in ${resendIn}s` : reqId ? "Resend code" : "Send code"}
              </Button>
            </Stack>
            <CodeChannelChoice value={codeTo} onChange={(c) => { setCodeTo(c); setReqId(""); setSentLine(""); }} disabled={!ns || sending} />
            {reqId && (
              <>
                <TextField label={sentLine || `Code sent to ${masked}`} size="small" autoComplete="one-time-code" value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 8)); save.reset(); }}
                  slotProps={{ htmlInput: { inputMode: "numeric", "data-testid": "password-sms-code" } }} />
                {!isTestCodeRequest(reqId) && (
                  <div>
                    <Button size="small" disabled={sending || resendIn > 0} onClick={() => void sendCode("voice")}>
                      Call me instead
                    </Button>
                  </div>
                )}
              </>
            )}
            {smsError && <Alert severity="error">{smsError}</Alert>}
          </>
        )}
        <PasswordField label="New password" autoComplete="new-password" size="small"
          value={next} onChange={(e) => { setNext(e.target.value); setTouched(true); save.reset(); }} disabled={!ns}
          error={touched && (Boolean(problem) || same)}
          helperText={touched && same ? "Choose a password different from the current one." : touched && problem ? problem : rules.data ? `Use ${describeRules(rules.data)}.` : " "} />
        <PasswordField label="New password again" autoComplete="new-password" size="small"
          value={again} onChange={(e) => { setAgain(e.target.value); save.reset(); }} disabled={!ns}
          error={mismatch} helperText={mismatch ? "The two new passwords are not the same." : " "} />
        <div>
          <Button type="submit" variant="contained" disabled={!ready}>
            {save.isPending ? "Changing…" : "Change password"}
          </Button>
        </div>
        {widgetCfg && (
          <Typography variant="body2" color="text.secondary">
            <Link component="button" type="button" data-testid="password-switch-proof"
              onClick={() => switchTo(by === "current" ? "sms" : "current")}>
              {by === "current" ? "Don’t know your current password? Use an SMS code instead" : "Use my current password instead"}
            </Link>
          </Typography>
        )}
        {save.isSuccess && (
          <Alert severity="success">Password changed. Use the new password the next time you sign in.</Alert>
        )}
        {save.isError && <Alert severity="error">{failure}</Alert>}
      </Stack>
    </Paper>
  );
}
